import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import Login from '@/pages/Login';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import AcceptInvite from '@/pages/AcceptInvite';
import MagicLinkVerify from '@/pages/MagicLinkVerify';
import Dashboard from '@/pages/Dashboard';
import Actions from '@/pages/Actions';
import ActionDetail from '@/pages/ActionDetail';
import Meetings from '@/pages/Meetings';
import MeetingDetail from '@/pages/MeetingDetail';
import MeetingManagement from '@/pages/MeetingManagement';
import ScheduledMeetingDetail from '@/pages/ScheduledMeetingDetail';
import OrgReportForm from '@/pages/OrgReportForm';
import MeetingMinutesEditor from '@/pages/MeetingMinutesEditor';
import Decisions from '@/pages/Decisions';
import Users from '@/pages/Users';
import Categories from '@/pages/Categories';
import Reports from '@/pages/Reports';
import Profile from '@/pages/Profile';
import AdminTools from '@/pages/AdminTools';
import Help from '@/pages/Help';
import Layout from '@/components/Layout';
import '@/App.css';

// Lazy load mobile app for code splitting
const MobileApp = lazy(() => import('@/mobile/MobileApp'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// Add Authorization header from localStorage as fallback when cookies fail
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthContext = React.createContext(null);
export const GroupContext = React.createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  // Fetch user's groups when user is authenticated
  useEffect(() => {
    if (user) {
      fetchUserGroups();
    } else {
      setGroups([]);
      setActiveGroup(null);
    }
  }, [user]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserGroups = async () => {
    try {
      const response = await axios.get(`${API}/auth/me/groups`);
      setGroups(response.data);
      
      // Set active group based on user's active_group_id or default
      if (response.data.length > 0) {
        const userActiveGroupId = user?.active_group_id;
        const defaultGroup = response.data.find(g => g.is_default);
        const activeGroupFromUser = response.data.find(g => g.id === userActiveGroupId);
        
        setActiveGroup(activeGroupFromUser || defaultGroup || response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch groups:', error);
    }
  };

  const switchGroup = async (groupId) => {
    try {
      await axios.post(`${API}/auth/me/switch-group`, { group_id: groupId });
      const newActiveGroup = groups.find(g => g.id === groupId);
      setActiveGroup(newActiveGroup);
      // Refresh user to update active_group_id
      await checkAuth();
      return true;
    } catch (error) {
      console.error('Failed to switch group:', error);
      return false;
    }
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    // Store session token in localStorage as fallback for cookie issues
    if (response.data.session_token) {
      localStorage.setItem('session_token', response.data.session_token);
    }
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    localStorage.removeItem('session_token');
    setUser(null);
    setGroups([]);
    setActiveGroup(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, checkAuth }}>
      <GroupContext.Provider value={{ groups, activeGroup, switchGroup, fetchUserGroups }}>
        <BrowserRouter>
          <Routes>
            {/* Mobile PWA Routes */}
            <Route path="/mobile/*" element={
              <Suspense fallback={
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                </div>
              }>
                <MobileApp />
              </Suspense>
            } />
            
            {/* Desktop Routes */}
            <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />
            <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" /> : <ForgotPassword />} />
            <Route path="/reset-password" element={user ? <Navigate to="/dashboard" /> : <ResetPassword />} />
            <Route path="/accept-invite" element={user ? <Navigate to="/dashboard" /> : <AcceptInvite />} />
            <Route path="/set-password" element={user ? <Navigate to="/dashboard" /> : <AcceptInvite />} />
            <Route path="/magic-login" element={user ? <Navigate to="/dashboard" /> : <MagicLinkVerify />} />
            <Route path="/" element={user ? <Layout /> : <Navigate to="/login" />}>
              <Route index element={<Navigate to="/dashboard" />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="actions" element={<Actions />} />
              <Route path="actions/:id" element={<ActionDetail />} />
              <Route path="meetings" element={<Meetings />} />
              <Route path="meetings/:id" element={<MeetingDetail />} />
              <Route path="meeting-management" element={<MeetingManagement />} />
              <Route path="scheduled-meeting/:id" element={<ScheduledMeetingDetail />} />
              <Route path="org-report/:meetingId/:orgId" element={<OrgReportForm />} />
              <Route path="meeting-minutes/:minutesId" element={<MeetingMinutesEditor />} />
              <Route path="decisions" element={<Decisions />} />
              <Route path="users" element={<Users />} />
              <Route path="categories" element={<Categories />} />
              <Route path="reports" element={<Reports />} />
              <Route path="profile" element={<Profile />} />
              <Route path="admin" element={<AdminTools />} />
              <Route path="help" element={<Help />} />
            </Route>
          </Routes>
          <Toaster position="bottom-right" richColors expand={false} />
        </BrowserRouter>
      </GroupContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;